class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.updateFreeShippingProgress();
  }

  async updateFreeShippingProgress() {
    const progress = this.querySelector('[data-free-shipping-progress]');
    if (!progress) return;

    try {
      const response = await fetch(`${routes.cart_url}.js`);
      if (!response.ok) throw new Error('Could not load cart');

      const cart = await response.json();
      const threshold = Number(progress.dataset.threshold || 15000);

      // Get product tags to exclude Shopify Collective products.
      const handles = [...new Set(cart.items.map((item) => item.handle))];

      const products = await Promise.all(
        handles.map(async (handle) => {
          const productResponse = await fetch(
            `${window.Shopify.routes.root}products/${encodeURIComponent(handle)}.js`
          );

          if (!productResponse.ok) {
            throw new Error(`Could not load product: ${handle}`);
          }

          return productResponse.json();
        })
      );

      const productTags = new Map(
        products.map((product) => [
          product.handle,
          (product.tags || []).map((tag) => tag.toLowerCase()),
        ])
      );

      // Calculate the subtotal for store products only.
      const ownCartTotal = cart.items.reduce((total, item) => {
        const tags = productTags.get(item.handle) || [];

        if (tags.includes('shopify collective')) {
          return total;
        }

        return total + Number(item.final_line_price || 0);
      }, 0);

      // Hide the progress bar if the cart contains only Collective products.
      const hasOwnProducts = cart.items.some((item) => {
        const tags = productTags.get(item.handle) || [];
        return !tags.includes('shopify collective');
      });

      if (!hasOwnProducts) {
        progress.hidden = true;
        return;
      }

      const remaining = Math.max(threshold - ownCartTotal, 0);
      const percentage = Math.min((ownCartTotal / threshold) * 100, 100);
      const currency = cart.currency || 'USD';

      const money = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
      }).format(remaining / 100);

      const message = progress.querySelector('[data-free-shipping-message]');
      const bar = progress.querySelector('[data-free-shipping-bar]');

      if (!message || !bar) return;

      if (remaining > 0) {
        message.textContent = `Spend ${money} more to unlock free shipping.`;
      } else {
        message.textContent = 'Free shipping unlocked!';
      }

      bar.style.width = `${percentage}%`;
      bar.parentElement.setAttribute('aria-valuenow', String(ownCartTotal));
      progress.hidden = false;
    } catch (error) {
      console.error('Free shipping progress error:', error);
      progress.hidden = true;
    } 
  }
  
  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
